import React, { FC } from 'react';
import { isEmpty, uniqueId } from 'lodash';
import { FieldArrayRenderProps, useFormikContext } from 'formik';
import { Button } from '@qbe/ads-core';
import { FormCard, Toggle } from '@qbe/ads-blocks-forms';
import { EmploymentData, EmploymentFormFields } from 'contexts/FormProvider';
import { YesNoList } from 'utils/constants';

import validationSchema from '../schema';
import FormSpacer from 'components/FormSpacer/FormSpacer';
import { useContent, useContentContext } from '@qbe/dfc-content';

interface Props {
  arrayHelpers: FieldArrayRenderProps;
}

const EmploymentStatus: FC<Props> = ({ arrayHelpers }) => {
  const { values, setFieldValue, setFieldTouched, resetForm } = useFormikContext<EmploymentFormFields>();

  const { getTextContent } = useContent({
    context: useContentContext(),
  });

  const isDisabled = !validationSchema.isValidSync(values);

  const isCurrentlyEmployed = values.employmentStatus === 'yes';

  // Localized content constants
  const sectionHeading = getTextContent('Employment.details.heading');
  const employmentStatusTitle = getTextContent('Employment.details.employmentStatus');
  const submitLabel = getTextContent('Employment.details.submit.label');

  return (
    <FormCard variant="sectionTitle" title={sectionHeading}>
      <Toggle
        title={employmentStatusTitle}
        required
        name="employmentStatus"
        optionList={YesNoList.map((option) => ({
          id: option.value,
          label: option.text,
          value: option.value,
          checked: values.employmentStatus === option.value,
          'data-track': 'employmentStatus',
          'data-track-value': 'capture',
          onBlur: () => setFieldTouched('employmentStatus', true),
          onChange: (event) => {
            const value = event.currentTarget.value;
            setFieldValue('employmentStatus', value);

            if (isEmpty(values.employmentData) && value === 'yes') {
              const newDetails: EmploymentData = {
                id: uniqueId(),
                employmentType: '',
                employerName: '',
                occupation: '',
                contactPerson: '',
                contactPersonNumber: '',
                salary: '',
              };

              arrayHelpers.push(newDetails);
            }

            if (!isEmpty(values.employmentData) && value === 'no') {
              resetForm({
                errors: {
                  employmentData: [],
                  employmentStatus: '',
                },
              });

              setFieldValue('employmentData', []);
              setFieldValue('employmentStatus', value);
            }
          },
        }))}
      />

      {!isCurrentlyEmployed && (
        <>
          <FormSpacer type="formgroup" />
          <Button type="submit" label={submitLabel} disabled={isDisabled} />
        </>
      )}
    </FormCard>
  );
};

export default EmploymentStatus;
